import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, Download, Search, ChevronDown, ChevronRight } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const KIND_GROUPS = [
    { label: "All", value: "" },
    { label: "Leads", value: "lead.*" },
    { label: "Payments", value: "payment_reminder.*" },
    { label: "Payment alerts", value: "payment_alert.*" },
    { label: "Carrier portal", value: "carrier_portal.*" },
    { label: "Pay-link clicks", value: "pay_link.clicked" },
    { label: "Policies (mark paid)", value: "policy.mark_paid" },
    { label: "ID Verification", value: "id_verification.*" },
];

const ENTITY_TYPES = [
    { label: "Any entity", value: "" },
    { label: "Lead", value: "lead" },
    { label: "Policy", value: "policy" },
    { label: "Payment reminder", value: "payment_reminder" },
    { label: "Payment alert", value: "payment_alert" },
    { label: "Carrier portal", value: "carrier_portal" },
    { label: "ID verification", value: "id_verification" },
];

const MAX_EXPORT_ROWS = 5000;

export default function AdminActivityLog() {
    const today = new Date().toISOString().slice(0, 10);
    const last7 = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

    const [filters, setFilters] = useState({
        from: last7,
        to: today,
        kind: "",
        entity_type: "",
        q: "",
    });
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(new Set());

    const load = useCallback(async (resetItems = true) => {
        setLoading(true);
        try {
            const params = {
                from: filters.from, to: filters.to,
                limit: 100,
                ...(filters.kind ? { kind: filters.kind } : {}),
                ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
                ...(filters.q ? { q: filters.q } : {}),
                ...(resetItems ? {} : (cursor ? { cursor } : {})),
            };
            const res = await adminApi.activityLog(params);
            setItems((prev) => resetItems ? (res.items || []) : [...prev, ...(res.items || [])]);
            setNextCursor(res.next_cursor || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load activity log.");
        } finally {
            setLoading(false);
        }
    }, [filters, cursor]);

    // Reload on filter change (always reset)
    useEffect(() => {
        setCursor(null);
        load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.from, filters.to, filters.kind, filters.entity_type, filters.q]);

    const loadMore = async () => {
        if (!nextCursor) return;
        setCursor(nextCursor);
        // Use the updated cursor by re-running load(false) — but load reads cursor from closure.
        // Inline the call here to avoid stale-closure issues.
        setLoading(true);
        try {
            const params = {
                from: filters.from, to: filters.to,
                limit: 100,
                cursor: nextCursor,
                ...(filters.kind ? { kind: filters.kind } : {}),
                ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
                ...(filters.q ? { q: filters.q } : {}),
            };
            const res = await adminApi.activityLog(params);
            setItems((prev) => [...prev, ...(res.items || [])]);
            setNextCursor(res.next_cursor || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load more.");
        } finally {
            setLoading(false);
        }
    };

    const toggleExpanded = (id) => {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const exportCsv = async () => {
        // Pull rows in pages until we reach MAX_EXPORT_ROWS or run out
        toast.message("Exporting…");
        let all = [];
        let c = null;
        for (let i = 0; i < 50; i++) {  // 50 * 100 = 5000 cap
            const params = {
                from: filters.from, to: filters.to,
                limit: 100,
                ...(c ? { cursor: c } : {}),
                ...(filters.kind ? { kind: filters.kind } : {}),
                ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
                ...(filters.q ? { q: filters.q } : {}),
            };
            try {
                const res = await adminApi.activityLog(params);
                all = all.concat(res.items || []);
                c = res.next_cursor;
                if (!c || all.length >= MAX_EXPORT_ROWS) break;
            } catch {
                break;
            }
        }
        const lines = ["Time,Actor,Kind,Entity Type,Entity ID,Summary"];
        all.slice(0, MAX_EXPORT_ROWS).forEach((r) => {
            lines.push([
                csvCell(r.ts || ""), csvCell(r.actor_name || ""),
                csvCell(r.kind || ""), csvCell(r.entity_type || ""),
                csvCell(r.entity_id || ""), csvCell(r.summary || ""),
            ].join(","));
        });
        downloadBlob(lines.join("\n"), `activity_log_${filters.from}_to_${filters.to}.csv`);
    };

    return (
        <div className="space-y-5" data-testid="admin-activity-log">
            <header>
                <div className="mm-eyebrow flex items-center gap-1.5"><Activity size={11} /> Audit</div>
                <h1 className="mm-display text-3xl text-mm-navy">Activity Log</h1>
                <p className="mt-1 text-sm text-mm-muted">Everything that's happened across leads, payments, and ID verification.</p>
            </header>

            <div className="mm-card flex flex-wrap items-end gap-3 p-4">
                <Field label="From">
                    <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="al-from" />
                </Field>
                <Field label="To">
                    <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="al-to" />
                </Field>
                <Field label="Domain">
                    <select value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="al-kind">
                        {KIND_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                </Field>
                <Field label="Entity">
                    <select value={filters.entity_type} onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="al-entity">
                        {ENTITY_TYPES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>
                </Field>
                <Field label="Search">
                    <div className="relative">
                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-mm-muted" />
                        <input
                            type="text"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            className="rounded-md border border-mm-line pl-7 pr-2 py-1.5 text-sm"
                            placeholder="kind, entity id, name…"
                            data-testid="al-search"
                        />
                    </div>
                </Field>
                <button onClick={exportCsv} className="mm-btn-outline ml-auto !py-1.5 text-xs" data-testid="al-csv">
                    <Download size={12} /> CSV (cap 5K)
                </button>
            </div>

            <div className="mm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                        <tr>
                            <th className="px-3 py-2.5 text-left font-medium">Time</th>
                            <th className="px-3 py-2.5 text-left font-medium">Actor</th>
                            <th className="px-3 py-2.5 text-left font-medium">Kind</th>
                            <th className="px-3 py-2.5 text-left font-medium">Entity</th>
                            <th className="px-3 py-2.5 text-left font-medium">Summary</th>
                            <th className="px-3 py-2.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && !loading && (
                            <tr><td colSpan={6} className="py-10 text-center text-mm-muted">No activity in this range.</td></tr>
                        )}
                        {items.map((row) => {
                            const isOpen = expanded.has(row.id);
                            return (
                                <React.Fragment key={row.id}>
                                    <tr
                                        className="cursor-pointer border-t border-mm-line hover:bg-mm-bg/40"
                                        onClick={() => toggleExpanded(row.id)}
                                        data-testid={`al-row-${row.id}`}
                                    >
                                        <td className="px-3 py-2 align-top text-[11px] text-mm-muted tabular-nums whitespace-nowrap">
                                            {row.ts ? new Date(row.ts).toLocaleString() : "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top text-mm-ink">
                                            {row.actor_name || "—"}
                                            {row.actor_role && <div className="text-[10px] text-mm-muted">{row.actor_role}</div>}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <code className="rounded bg-mm-bg px-1.5 py-0.5 text-[10px] text-mm-navy">{row.kind}</code>
                                        </td>
                                        <td className="px-3 py-2 align-top text-[12px]">
                                            {row.entity_type && <span className="text-mm-muted">{row.entity_type}</span>}
                                            {row.entity_id && <div className="text-[10px] text-mm-muted">{String(row.entity_id).slice(0, 12)}…</div>}
                                        </td>
                                        <td className="px-3 py-2 align-top text-mm-ink">{row.summary}</td>
                                        <td className="px-3 py-2 align-top">
                                            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="border-t border-mm-line bg-mm-bg/30">
                                            <td colSpan={6} className="px-4 py-3">
                                                <pre className="overflow-x-auto rounded-md bg-white p-3 text-[11px] text-mm-ink border border-mm-line" data-testid={`al-meta-${row.id}`}>
{JSON.stringify(row.metadata || {}, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                {nextCursor && (
                    <div className="border-t border-mm-line bg-mm-bg/20 px-4 py-3 text-center">
                        <button
                            onClick={loadMore}
                            disabled={loading}
                            className="mm-btn-outline !py-1.5 text-xs disabled:opacity-50"
                            data-testid="al-load-more"
                        >
                            {loading ? <Loader2 size={12} className="animate-spin" /> : null}
                            Load more
                        </button>
                    </div>
                )}
                {loading && items.length === 0 && (
                    <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-mm-muted">{label}</span>
            {children}
        </label>
    );
}

function csvCell(s) { if (s == null) return ""; const str = String(s); return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str; }
function downloadBlob(data, filename) {
    const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
